<script setup lang="ts">
// Numeric range filter. Two `<input type="number">` controls side by
// side covering min and max bounds. Emits `[min, max]` whenever both
// inputs hold a number; either-empty state emits `null`.
//
// Bounds are held as local refs (not computed off `props.value`) so the
// user can fill one input before the other without their entry being
// wiped: the partial state emits `null` upstream, but the local refs
// keep the half-entered value visible. A `props.value` watcher syncs
// external resets back into the local state, with a one-shot suppression
// flag to ignore the echo from our own partial-`null` emit (which would
// otherwise clobber the other input).

import { computed, ref, useId, watch } from "vue";

import type { NumberRangeOpts } from "../sbi/protocol";
import { usePrintMode } from "../sbi/print-mode.ts";
import PrintStaticControl from "./PrintStaticControl.vue";
import ControlLabel from "./ControlLabel.vue";

const props = defineProps<{
  id: string;
  value: readonly [number, number] | null;
  opts?: NumberRangeOpts;
}>();

const emit = defineEmits<{
  (
    e: "change-target",
    id: string,
    value: [number, number] | null,
  ): void;
}>();

const lo = ref<number | null>(props.value?.[0] ?? null);
const hi = ref<number | null>(props.value?.[1] ?? null);

const isPrint = usePrintMode();

// Label wiring: ControlLabel puts `labelId` on the label text.
const labelId = useId();

// Print-mode display: `lo – hi` when both bounds are set, otherwise a
// placeholder. En-dash matches typographic convention for numeric
// ranges; the live UI uses two separate inputs so it has no equivalent
// glyph.
const printValue = computed(() => {
  if (lo.value === null || hi.value === null) return "(not set)";
  return `${lo.value} – ${hi.value}`;
});

// Set just before emitting a partial-`null` when the parent currently
// holds a non-null pair: the resulting prop change is our own echo and
// must not overwrite the half the user just typed.
let suppressNextPropSync = false;

watch(
  () => props.value,
  (v) => {
    if (suppressNextPropSync) {
      suppressNextPropSync = false;
      return;
    }
    lo.value = v?.[0] ?? null;
    hi.value = v?.[1] ?? null;
  },
);

function parse(raw: string): number | null {
  if (raw === "") return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function emitPair(): void {
  if (lo.value === null || hi.value === null) {
    if (props.value !== null) suppressNextPropSync = true;
    emit("change-target", props.id, null);
    return;
  }
  emit("change-target", props.id, [lo.value, hi.value]);
}

function onMin(e: Event): void {
  lo.value = parse((e.target as HTMLInputElement).value);
  emitPair();
}
function onMax(e: Event): void {
  hi.value = parse((e.target as HTMLInputElement).value);
  emitPair();
}
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <div v-else class="number-range">
    <ControlLabel
      v-if="opts?.label || opts?.description"
      class="number-range-label"
      :label="opts?.label"
      :description="opts?.description"
      :label-id="labelId"
    />
    <div
      class="inputs"
      role="group"
      :aria-labelledby="opts?.label ? labelId : undefined"
    >
      <input
        type="number"
        :value="lo ?? ''"
        :min="opts?.min"
        :max="opts?.max"
        :step="opts?.step"
        aria-label="Minimum"
        @input="onMin"
      />
      <span class="sep" aria-hidden="true">–</span>
      <input
        type="number"
        :value="hi ?? ''"
        :min="opts?.min"
        :max="opts?.max"
        :step="opts?.step"
        aria-label="Maximum"
        @input="onMax"
      />
    </div>
  </div>
</template>

<style scoped>
/* Never wider than the slot it sits in; full width when that slot is
   narrow (filter bar < 480px, a narrow column cell). */
.number-range {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
@container (max-width: 479px) {
  .number-range {
    display: flex;
    width: 100%;
  }
}
.number-range-label {
  color: var(--color-text-muted);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
}
/* Narrow: two equal columns either side of the separator, so the pair
   never overflows the slot (two native date inputs need ~310px). */
@container (max-width: 479px) {
  .inputs {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    width: 100%;
  }
  .inputs input[type] {
    width: 100%;
  }
}
.sep {
  color: var(--color-text-muted);
  font-size: 12px;
}
input[type="number"] {
  background: var(--card);
  color: var(--card-ink);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  padding: 6px 10px;
  font: inherit;
  flex: 0 1 8ch;
  width: 8ch;
  min-width: 0;
  box-sizing: border-box;
}
input[type="number"]:focus {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}
/* Touch: 16px text (iOS zooms the page when a smaller input takes
   focus) and 40–44px targets. Desktop keeps the 13px density. */
@media (pointer: coarse) {
  input[type="number"] {
    min-height: 44px;
    font-size: 16px;
  }
}
</style>
